/**
 * Notification SSE React Hook
 *
 * Wraps the pure SSE service with React state management.
 * Pushes SSE events into TanStack Query cache for real-time updates.
 * Fires toast notifications for high-priority events.
 *
 * Follows the same pattern as entities/user/hooks/useVerificationStatusSSE.ts.
 *
 * @module entities/notification/hooks/useNotificationSSE
 */

import { useState, useCallback, useEffect, useRef } from 'react';
import { useQueryClient, type InfiniteData } from '@tanstack/react-query';
import { notificationQueryKeys } from '@lib/query/client';
import { createNotificationSSEConnection } from '../services/notificationSSE';
import {
  prependNotificationToCache,
  markNotificationReadInCache,
  markAllReadInCache,
} from './useNotificationQueries';
import type {
  NotificationSSEConnectionState,
  NotificationSSECallbacks,
  NotificationSSEConnection,
} from '../types/notificationSSE.types';
import type {
  Notification,
  NotificationListResponse,
  UnreadCountResponse,
} from '../types';

// ─────────────────────────────────────────────────────────────────────────────
// Token Constants
// ─────────────────────────────────────────────────────────────────────────────

const ACCESS_TOKEN_KEY = 'amalq_user_access_token';

// ─────────────────────────────────────────────────────────────────────────────
// Types
// ─────────────────────────────────────────────────────────────────────────────

type NotificationPages = InfiniteData<NotificationListResponse>;

export interface UseNotificationSSEOptions {
  /** Callback when a new notification arrives (for toast display) */
  onNewNotification?: (notification: Notification) => void;
  /** Callback when connection fails permanently */
  onConnectionError?: (error: Error) => void;
}

export interface UseNotificationSSEReturn {
  /** Current SSE connection state */
  connectionState: NotificationSSEConnectionState;
  /** Whether connected successfully */
  isConnected: boolean;
  /** Start or restart the SSE connection */
  connect: () => void;
  /** Close the SSE connection */
  disconnect: () => void;
}

// ─────────────────────────────────────────────────────────────────────────────
// Hook Implementation
// ─────────────────────────────────────────────────────────────────────────────

/**
 * React hook for managing the notification SSE connection
 *
 * Integrates with TanStack Query cache: SSE events push directly into
 * notification list and unread count caches, providing real-time updates
 * and multi-tab sync without extra API calls.
 *
 * @param options - Optional callbacks for new notifications and errors
 */
export function useNotificationSSE(
  options: UseNotificationSSEOptions = {}
): UseNotificationSSEReturn {
  const [connectionState, setConnectionState] =
    useState<NotificationSSEConnectionState>('disconnected');

  const queryClient = useQueryClient();
  const connectionRef = useRef<NotificationSSEConnection | null>(null);

  // Keep callbacks ref in sync to avoid stale closures
  const callbacksRef = useRef(options);
  useEffect(() => {
    callbacksRef.current = options;
  });

  const disconnect = useCallback(() => {
    if (connectionRef.current) {
      connectionRef.current.close();
      connectionRef.current = null;
    }
  }, []);

  const connect = useCallback(() => {
    disconnect();

    const token = localStorage.getItem(ACCESS_TOKEN_KEY);
    if (!token) {
      callbacksRef.current.onConnectionError?.(
        new Error('No access token available')
      );
      return;
    }

    const callbacks: NotificationSSECallbacks = {
      onStateChange: setConnectionState,

      onConnected: (data) => {
        // Sync initial unread count from SSE connected event
        queryClient.setQueryData<UnreadCountResponse>(
          notificationQueryKeys.notifications.unreadCount(),
          { count: data.unreadCount }
        );
      },

      onNotification: (notification) => {
        // Prepend to all list caches
        queryClient.setQueriesData<NotificationPages>(
          { queryKey: notificationQueryKeys.notifications.lists() },
          (old) =>
            old ? prependNotificationToCache(old, notification) : old
        );

        // Increment unread count
        queryClient.setQueryData<UnreadCountResponse>(
          notificationQueryKeys.notifications.unreadCount(),
          (old) => ({ count: (old?.count ?? 0) + 1 })
        );

        // Notify parent component (for toast display)
        callbacksRef.current.onNewNotification?.(notification);
      },

      onRead: (data) => {
        // Update notification in all list caches (multi-tab sync)
        queryClient.setQueriesData<NotificationPages>(
          { queryKey: notificationQueryKeys.notifications.lists() },
          (old) =>
            old ? markNotificationReadInCache(old, data.id) : old
        );

        // Decrement unread count
        queryClient.setQueryData<UnreadCountResponse>(
          notificationQueryKeys.notifications.unreadCount(),
          (old) => ({ count: Math.max((old?.count ?? 1) - 1, 0) })
        );
      },

      onReadAll: () => {
        // Mark all cached notifications as read
        queryClient.setQueriesData<NotificationPages>(
          { queryKey: notificationQueryKeys.notifications.lists() },
          (old) => (old ? markAllReadInCache(old) : old)
        );

        // Set unread count to zero
        queryClient.setQueryData<UnreadCountResponse>(
          notificationQueryKeys.notifications.unreadCount(),
          { count: 0 }
        );
      },

      onError: (error, code) => {
        if (code === 'MAX_RETRIES_EXCEEDED') {
          callbacksRef.current.onConnectionError?.(error);
        }
      },
    };

    connectionRef.current = createNotificationSSEConnection(token, callbacks);
  }, [disconnect, queryClient]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      disconnect();
    };
  }, [disconnect]);

  return {
    connectionState,
    isConnected: connectionState === 'connected',
    connect,
    disconnect,
  };
}
